import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { Button, Icons, Modal } from 'utoniq-ui'

import { useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'

export type NftCreateModalProps = {
  open: boolean
  onClose: () => void
  steps: StepProps['step'][]
  error?: string
  onRetry?: () => void
}

export enum CreateNftStatus {
  Error = -1,
  Creating = 0,
  Completed = 1,
}

export const NftCreateModal: Organism<NftCreateModalProps> = ({
  open,
  onClose,
  steps,
  error,
  onRetry,
}) => {
  const { t } = useTranslation('nftCreateModal')
  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          width: 220,
          borderRadius: 3,
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'grey.100',
          outline: 'none',
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        <Stack sx={{ py: 6 }} alignItems="center">
          <Typography variant="subtitle1">{t('title')}</Typography>
          <Stack spacing={4} sx={{ pt: 4 }}>
            {steps.map((step, index) => (
              <Step index={index + 1} key={index} step={step} />
            ))}
          </Stack>
          {!!error && (
            <Stack sx={{ pt: 5, px: 5 }} alignItems="center">
              <Typography variant="caption" sx={{ color: 'error.main' }}>
                {error}
              </Typography>
              <Button size="small" sx={{ mt: 3, width: 144 }} onClick={onRetry}>
                {t('retry')}
              </Button>
            </Stack>
          )}
          <Button
            size="small"
            variant="text"
            sx={{ color: 'text.secondary', mt: 3 }}
            onClick={onClose}
          >
            {t('cancel')}
          </Button>
        </Stack>
      </Box>
    </Modal>
  )
}

type StepProps = {
  index: number
  step: {
    status: CreateNftStatus
    label: string
  }
}
const Step: Cell<StepProps> = ({ index, step }) => (
  <CellFrame sx={{ width: 140 }}>
    <Stack direction="row" spacing={3} alignItems="center">
      {step.status === CreateNftStatus.Error && (
        <Icons.Error fontSize="large" sx={{ color: 'error.main' }} />
      )}
      {step.status === CreateNftStatus.Creating && (
        <CircularProgress size={31} sx={{ color: 'grey.500' }} />
      )}
      {step.status === CreateNftStatus.Completed && (
        <Icons.Complete fontSize="large" sx={{ color: 'info.main' }} />
      )}
      <Stack flex={1} justifyItems="flex-start">
        <Typography variant="subtitle2" color="text.secondary" align="left">
          STEP {index}
        </Typography>
        <Typography variant="subtitle1" align="left">
          {step.label}
        </Typography>
      </Stack>
    </Stack>
  </CellFrame>
)
